import { ArrowLeft } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { AtterraggioRuolo } from "@/components/admin/AtterraggioRuolo";
import { PermessiMatrice } from "@/components/admin/PermessiMatrice";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminRuoloPermessi,
  usePutAdminRuoloPermessi,
} from "@/hooks/useAdminPermessi";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import type { Ruolo } from "@/types/api";

const RUOLI_VALIDI: readonly Ruolo[] = [
  "mp_presidenza",
  "mp_management",
  "mp_planning",
  "mp_logistica",
  "mp_commerciale",
  "mp_viewer",
];

const RUOLO_LABEL: Record<Ruolo, string> = {
  maitime_staff: "Maitime Staff",
  mp_presidenza: "Presidenza",
  mp_management: "Management",
  mp_planning: "Planning",
  mp_logistica: "Logistica",
  mp_commerciale: "Commerciale",
  mp_viewer: "Viewer",
};

export function AdminRuoloPermessiPage() {
  const { ruolo } = useParams<{ ruolo: string }>();
  const { data: me, isLoading: loadingMe } = useMe();

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  if (!ruolo || !RUOLI_VALIDI.includes(ruolo as Ruolo)) {
    return <Navigate to="/admin/ruoli" replace />;
  }

  const ruoloEnum = ruolo as Ruolo;

  return <Body ruolo={ruoloEnum} />;
}

function Body({ ruolo }: { ruolo: Ruolo }) {
  const { data, isLoading } = useAdminRuoloPermessi(ruolo);
  const mutation = usePutAdminRuoloPermessi(ruolo);

  return (
    <PageContainer>
      <Link
        to="/admin/ruoli"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Ruoli
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Preset di ruolo
        </p>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {RUOLO_LABEL[ruolo]}
          </h1>
          {data && (
            <Badge
              variant="outline"
              className={
                data.configurato
                  ? "bg-success/10 text-success border-success/30"
                  : "bg-muted text-muted-foreground"
              }
            >
              {data.configurato ? "Configurato" : "Default legacy"}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Permessi di default per tutti gli utenti con ruolo{" "}
          <span className="font-mono">{ruolo}</span>. Possono essere
          sovrascritti per singolo utente da "Utenti e accessi".
        </p>
      </header>

      {isLoading || !data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          {/* L'atterraggio sta **sopra** la matrice: è la prima cosa che una
              persona vede quando fa login, mentre i permessi sono ciò che poi
              trova. Sotto, si scopriva scorrendo. */}
          <AtterraggioRuolo
            ruolo={ruolo}
            valore={data.atterraggio}
            righe={data.righe}
            salvando={mutation.isPending}
            salvato={mutation.isSuccess}
            // ⚠️ Si rimandano **anche le righe correnti**: il PUT sostituisce
            // tutto il preset, quindi salvare il solo atterraggio senza di esse
            // cancellerebbe i permessi del ruolo.
            onSalva={(atterraggio) =>
              mutation.mutateAsync({ righe: data.righe, atterraggio })
            }
          />
          <PermessiMatrice
            righeIniziali={data.righe}
            mode="preset"
            // Il salvataggio della matrice **non nomina** l'atterraggio, quindi
            // lo lascia dov'è: due manopole indipendenti nella stessa pagina.
            onSave={(righe) => mutation.mutateAsync({ righe })}
            saving={mutation.isPending}
          />
        </>
      )}

      {mutation.isError && (
        <p className="text-xs text-destructive">
          Errore salvataggio: {(mutation.error as Error).message}
        </p>
      )}
    </PageContainer>
  );
}
